import React, { useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button, Table, Switch, Tag, Row, Typography, Input, Icon,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfigHeading from '../components/ConfigHeading';
import {
  editDamageArea as editDamageAreaAction, requestDamageAreasPageData, requestPublishDamageArea,
} from '../../../../state/ui/configuration/inspection/damageAreaUiActions';
import { selectLoadingDamageAreaCodes, selectDamageAreasPageDataRequest } from '../../../../state/ui/configuration/inspection/damageAreaUiReducer';
import EditDamageArea from './EditDamageArea';
import { selectDamageAreas } from '../../../../state/domain/damageAreas/damageAreasReducer';
import { filterPublished } from './config-util';
import { userIsAdmin } from '../company/user-util';
import routeLocations from '../../../../utility/route-locations';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';


const { Column } = Table;
const { Text } = Typography;


// EXTRA FILTERS
function filterLocation(value, record) {
  return record.damageAreaLocationType === value;
}

// SORTS
function sortAreasByDesc(a, b) {
  const aStr = `${a.damageAreaDescription}`;
  const bStr = `${b.damageAreaDescription}`;
  return aStr.localeCompare(bStr);
}

function sortAreasByLocation(a, b) {
  const aStr = `${a.damageAreaLocation}`;
  const bStr = `${b.damageAreaLocation}`;
  return aStr.localeCompare(bStr);
}

function DamageAreasTable({
  loading, areas, onActiveUpdate, onEditButton, loadingAreaCodes, sessionUser,
}) {
  const intl = useIntl();

  const isActiveLoading = useCallback(area => !!loadingAreaCodes.find(code => `${code}` === `${area.damageAreaCode}`),
    [loadingAreaCodes]);

  const RowTotal = () => (
    <div>
      <Text>{`${areas.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  function getColumnSearchProps(dataIndex, searchBoxText) {
    let searchInput = () => {};
    const searchBoxBackGroundText = `${intl.formatMessage({ id: 'search' })} ${searchBoxText}`;

    return {
      filterDropdown: ({
        setSelectedKeys, selectedKeys, confirm, clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => { searchInput = node; }}
            placeholder={searchBoxBackGroundText}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            icon="search"
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {intl.formatMessage({ id: 'search' })}
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            {intl.formatMessage({ id: 'reset' })}
          </Button>
        </div>
      ),
      filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const words = record[dataIndex].split(' ');
        return words.find(word => word.toLowerCase() === value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.select());
        }
      },
    };
  }

  // TABLE SETTINGS, COLUMNS
  return (
    <Table
      rowKey={area => area.damageAreaCode}
      loading={loading}
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      dataSource={areas}
    >
      <Column
        title={<FormattedMessage id="published" />}
        dataIndex="active"
        key="active"
        filtered
        filters={[{ text: <FormattedMessage id="published" />, value: true }, { text: <FormattedMessage id="notPublished" />, value: false }]}
        filteredValue={areas.active}
        onFilter={filterPublished}
        width={110}
        render={(active, record) => (
          <Row type="flex" justify="center">
            { ((userIsAdmin(sessionUser) && record.damageAreaLocationType !== 'EXTERIOR DIAGRAM'))
              ? <Switch loading={isActiveLoading(record)} onChange={() => onActiveUpdate(record)} size="small" checked={active} />
              : <Tag color={active ? 'blue' : 'volcano'}><FormattedMessage id={active ? 'published' : 'notPublished'} /></Tag> }
          </Row>
        )}
      />
      <Column
        defaultSortOrder="ascend"
        sorter={sortAreasByDesc}
        title={<FormattedMessage id="description" />}
        width="50%"
        dataIndex="damageAreaDescription"
        key="damageAreaDescription"
        {...getColumnSearchProps('damageAreaDescription', intl.formatMessage({ id: 'description' }))}
      />
      <Column
        sorter={sortAreasByLocation}
        title={<FormattedMessage id="type" />}
        width="25%"
        dataIndex="damageAreaLocationType"
        key="damageAreaLocationType"
        filtered
        filters={[{ text: <FormattedMessage id="interiorUserDefined" />, value: 'INTERIOR USER-DEFINED' },
          { text: <FormattedMessage id="exteriorUserDefined" />, value: 'EXTERIOR USER-DEFINED' },
          { text: <FormattedMessage id="exteriorDiagram" />, value: 'EXTERIOR DIAGRAM' },
        ]}
        onFilter={filterLocation}
        render={(text, area) => (<Tag color={area.damageAreaLocationType === 'EXTERIOR DIAGRAM' ? 'purple' : 'blue'}>{text}</Tag>)}
      />
      { userIsAdmin(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="right"
            width={70}
            render={(text, record) => (
              <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                <FormattedMessage id="edit" />
              </Button>
            )}
          />
        ) : ''
      }
    </Table>
  );
}

DamageAreasTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  onActiveUpdate: PropTypes.func.isRequired,
  onEditButton: PropTypes.func.isRequired,
  areas: PropTypes.arrayOf(
    PropTypes.shape({
      damageAreaCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      damageAreaDescription: PropTypes.string.isRequired,
      damageAreaLocation: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
    }).isRequired,
  ),
  loadingAreaCodes: PropTypes.arrayOf(PropTypes.string),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  setSelectedKeys: PropTypes.string,
  selectedKeys: PropTypes.string,
  confirm: PropTypes.string,
  clearFilters: PropTypes.string,
};

DamageAreasTable.defaultProps = {
  areas: [],
  loadingAreaCodes: [],
  setSelectedKeys: null,
  selectedKeys: null,
  confirm: null,
  clearFilters: null,
};


function DamageAreas({
  isLoading, loadPageData, areas, allAreas, loadingAreaCodes, editDamageArea,
  updateDamageArea, sessionUser,
}) {
  // Load damage areas, damage types and model types on first entry.
  // These only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Extra validation helper
  const doesDescriptionExist = (code, value) => {
    const matchedArea = allAreas.find(row => row.damageAreaDescription.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedArea ? matchedArea.damageAreaCode !== code : false;
  };

  // TITLE ROW
  const CreateButton = () => (
    <Button type="primary" onClick={() => editDamageArea(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  // MAIN COMPONENT
  return (
    <ConfigHeading
      titleId="damageAreas"
      descriptionId="damagesDesc"
      actions={(
        userIsAdmin(sessionUser)
          ? <CreateButton />
          : <></>
      )}
      links={[(
        <Link to={routeLocations.configure_damage_types}>
          <FormattedMessage id="damageTypes" />
        </Link>
      )]}
    >
      <DamageAreasTable
        loading={isLoading}
        areas={areas}
        loadingAreaCodes={loadingAreaCodes}
        onActiveUpdate={updateDamageArea}
        onEditButton={editDamageArea}
        sessionUser={sessionUser}
      />
      <EditDamageArea descValidator={doesDescriptionExist} />
    </ConfigHeading>
  );
}


/**
 * Validation for the props on the component.
 */
DamageAreas.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  editDamageArea: PropTypes.func.isRequired,
  updateDamageArea: PropTypes.func.isRequired,
  areas: PropTypes.arrayOf(
    PropTypes.shape({
      damageAreaCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      damageAreaDescription: PropTypes.string.isRequired,
      damageAreaLocation: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
    }),
  ),
  allAreas: PropTypes.arrayOf(
    PropTypes.shape({
      damageAreaCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      damageAreaDescription: PropTypes.string.isRequired,
      damageAreaLocation: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
    }),
  ),
  loadingAreaCodes: PropTypes.arrayOf(PropTypes.string),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

/**
 * Default data for the component if nothing has been supplied.
 * Only needed for non-required props.
 */
DamageAreas.defaultProps = {
  isLoading: false,
  areas: null,
  allAreas: null,
  loadingAreaCodes: [],
};


/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectDamageAreasPageDataRequest(state);
  const damageAreas = selectDamageAreas(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    areas: damageAreas,
    allAreas: damageAreas || [],
    loadingAreaCodes: selectLoadingDamageAreaCodes(state),
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestDamageAreasPageData()),
  editDamageArea: area => dispatch(editDamageAreaAction(area ? area.damageAreaCode : null)),
  updateDamageArea: area => dispatch(requestPublishDamageArea(area)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(DamageAreas);
